/**
 * PKCE (Proof Key for Code Exchange) utility functions for OAuth 2.0
 * Used for secure authorization code flow without client secret
 */

/**
 * Encodes an ArrayBuffer to a Base64-URL safe string.
 * Base64-URL encoding replaces '+' with '-', '/' with '_', and removes padding '='.
 * 
 * @param buffer - The ArrayBuffer to encode
 * @returns A Base64-URL encoded string
 */
function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  // Convert to Base64-URL: replace + with -, / with _, and remove padding =
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Generates a cryptographically secure, URL-safe random string
 * for use as a code verifier in PKCE flow.
 * 
 * @returns A URL-safe string of 43-128 characters
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

/**
 * Generates a code challenge from a code verifier using SHA256 hashing
 * and Base64-URL encoding.
 * 
 * @param verifier - The code verifier string
 * @returns A promise that resolves to the Base64-URL encoded SHA256 hash
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

